import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { erasedSentence, retentionProblem, MAX_RETENTION_DAYS } from '../lib/retention'
import { explain } from '../lib/staffErrors'
import { nameIn, type StaffKey } from '../lib/staffText'
import type { FieldSurface, FieldType, VisitField } from '../lib/types'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { LoadingLine, MoveButtons, NamesField, cleanNames, hasName, reorder } from './recordParts'
import { ChoiceSelect, IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, SettingRow, SettingsCard, useOpenRecord } from './settingsKit'
import { Confirm, Toggle, useFlash, useSave } from './sectionKit'
import { useMayRead } from '../ui/Session'
import { EmptyState } from '../ui/parts'
import '../styles/orgset.css'

/**
 * Customer details: what a ticket may carry beyond its number.
 *
 * A ticket holds no identity by default — that is the right default, and this
 * screen is the narrow, declared reversal of it. Every row here is a decision an
 * organisation is making about personal data, not a layout to fill in, so each row
 * says what kind of answer it is, where it is shown and how long it is kept, and
 * the three things this screen has to get right are all here to stop that
 * decision being made by accident:
 *
 * The key is typed once and never again. A layout references a field by key, so
 * offering a rename here would silently blank every design that used it — the
 * server already refuses to apply one sent in a PATCH, and this screen does not
 * pretend otherwise by showing an input for it.
 *
 * Where it is shown is four switches, not a multi-select, because each one is its
 * own decision with its own consequence, and a multi-select is where those get
 * made without being read.
 *
 * Personal is a record, not a restriction. It changes nothing about where a value
 * is shown — the switches already decided that — it is how this installation
 * answers, later, what personal data it holds.
 *
 * Which form asks a field (the kiosk, reception) is decided by the designs that
 * reference its key, not here, which is why this screen has no form preview.
 */

const FIELD_TYPES: Array<{ value: FieldType; label: StaffKey }> = [
  { value: 'text', label: 'record.fields.type.text' },
  { value: 'number', label: 'record.fields.type.number' },
  { value: 'date', label: 'record.fields.type.date' },
  { value: 'boolean', label: 'record.fields.type.boolean' },
  { value: 'choice', label: 'record.fields.type.choice' },
  { value: 'phone', label: 'record.fields.type.phone' },
]

const SURFACES: Array<{ value: FieldSurface; label: StaffKey; short: StaffKey; hint?: StaffKey }> = [
  { value: 'ticket', label: 'record.fields.surface.ticket', short: 'record.fields.on.ticket', hint: 'record.fields.surface.ticketHint' },
  { value: 'counter', label: 'record.fields.surface.counter', short: 'record.fields.on.counter' },
  { value: 'display', label: 'record.fields.surface.display', short: 'record.fields.on.display' },
  { value: 'report', label: 'record.fields.surface.report', short: 'record.fields.on.report' },
]

/** The server's catalog.ValidFieldKey: up to three dot-separated parts of
 *  lowercase letters and digits, at most 64 long, and not under "qms". */
const validKey = (key: string) =>
  key.length <= 64 && /^[a-z0-9]+(\.[a-z0-9]+){0,2}$/.test(key) && key.split('.')[0] !== 'qms'

/** An example key, the same in every language. */
const EXAMPLE_KEY = 'visitor.phone'

// Since a626f26, GET /fields answers a field declared for no surface with
// `"surfaces": []`, not null. This guard is kept for a create response and an
// older server, both of which can still answer `"surfaces": null`, so every line
// below can treat `surfaces` as an array without re-checking.
function normalize(list: VisitField[]): VisitField[] {
  return list.map((f) => ({ ...f, surfaces: f.surfaces ?? [] }))
}

type Draft = Partial<VisitField> & { name: Record<string, string>; surfaces: FieldSurface[] }

const blank = (): Draft => ({
  key: '',
  name: {},
  type: 'text',
  personal: false,
  surfaces: ['counter'],
  maxLen: 0,
  choices: [],
  retentionDays: 0,
  sortOrder: 0,
  active: true,
})

type View = 'active' | 'off'

const TYPE_ICON: Partial<Record<FieldType, IconName>> = {
  text: 'edit',
  number: 'grid',
  date: 'clock',
  boolean: 'check',
  choice: 'layers',
  phone: 'phone',
}

const inOrder = (rows: VisitField[]) =>
  [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key))

export function Fields({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const [fields, setFields] = useState<VisitField[]>([])
  // False until the first answer, so a slow line reads as Loading, not as empty.
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [view, setView] = useState<View>('active')

  const load = useCallback(async () => {
    const listed = await api.fields(organisation)
    setFields(normalize(listed.fields))
    setLoaded(true)
  }, [organisation])

  const { flash, toast } = useFlash()
  const { busy, error, setError, save } = useSave(load, flash)

  useEffect(() => {
    setLoaded(false)
    load().catch((err) => {
      setLoadError(explain(err, lang, 'catalogue.fields.notLoaded'))
      setLoaded(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  const active = inOrder(fields.filter((f) => f.active))
  const off = inOrder(fields.filter((f) => !f.active))
  // Back to the list in use once the last switched-off field is gone.
  useEffect(() => {
    if (off.length === 0 && view === 'off') setView('active')
  }, [off.length, view])

  // The field open on its own page (#fields/<id>, #fields/new).
  const rec = useOpenRecord(fields, (f) => f.id, loaded)
  const open = (field?: VisitField) => {
    setError('')
    rec.open(field)
  }
  const editing = rec.isNew
    ? loaded && { field: blank(), isNew: true }
    : rec.row && { field: { ...rec.row, name: { ...rec.row.name } } as Draft, isNew: false }
  const move = (id: string, by: number) =>
    void save(async () => {
      for (const row of reorder(active, id, by)) await api.updateField(row.id, { sortOrder: row.sortOrder })
    }, say('record.orderSaved'))

  const typeLabel = (type: string) => {
    const key = FIELD_TYPES.find((x) => x.value === type)?.label
    // A server newer than this page can send a type it has not heard of; the row
    // says the raw value rather than failing to draw the list.
    return key ? say(key) : type
  }
  const shownOn = (surfaces: FieldSurface[]) => {
    const list = surfaces
      .map((s) => {
        const key = SURFACES.find((x) => x.value === s)?.short
        return key ? say(key) : s
      })
      .join(', ')
    const said = list || say('record.fields.shownNowhere')
    return said.charAt(0).toLocaleUpperCase() + said.slice(1)
  }
  const kept = (days?: number) => (days ? say('record.fields.keptDays', { n: days }) : say('record.fields.keptWithTicket'))

  const shown = view === 'off' ? off : active
  const row = (f: VisitField, i: number, list: VisitField[], movable: boolean) => (
    <ListRow key={f.id} actions off={!f.active} onOpen={() => open(f)}>
      <NameCell
        tile={<IconTile icon={TYPE_ICON[f.type] ?? 'edit'} tone={f.personal ? 4 : 1} />}
        name={
          <>
            {nameIn(f.name, lang, f.key)}
            {f.personal && (
              <Chip size="sm" variant="soft">
                {say('record.fields.personalTag')}
              </Chip>
            )}
          </>
        }
        sub={
          <>
            <span className="record-key">{f.key}</span> · {typeLabel(f.type)}
          </>
        }
      />
      <span className="rec-cell" data-one={f.surfaces.length > 0 || undefined} role="cell">
        {shownOn(f.surfaces)}
      </span>
      <span className="rec-cell" data-warn={f.personal && !f.retentionDays ? true : undefined} role="cell">
        {kept(f.retentionDays)}
      </span>
      <span role="cell">
        {movable && (
          <MoveButtons
            name={nameIn(f.name, lang, f.key)}
            first={i === 0}
            last={i === list.length - 1}
            disabled={busy}
            onMove={(by) => move(f.id, by)}
          />
        )}
      </span>
    </ListRow>
  )

  if (editing) {
    return (
      <>
        {toast}
        <FieldDrawer
          key={rec.code}
          editing={editing}
          all={fields}
          busy={busy}
          error={error}
          onClose={rec.close}
          onSave={(given) => {
            const draft = { ...given, name: cleanNames(given.name) }
            void save(
              () =>
                editing.isNew
                  ? api.createField({
                      ...draft,
                      sortOrder: (active.at(-1)?.sortOrder ?? 0) + 10,
                      ...(organisation ? { organisation } : {}),
                    })
                  : api.updateField(draft.id!, draft),
              editing.isNew ? say('record.fields.added', { name: nameIn(draft.name, lang, draft.key ?? '') }) : say('record.saved'),
            ).then((ok) => ok && rec.close())
          }}
          onDelete={(f) =>
            void save(() => api.deleteField(f.id!), say('record.fields.deleted', { name: nameIn(f.name, lang, f.key ?? '') })).then(
              (ok) => ok && rec.close(),
            )
          }
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.fields')}
        line={say('settings.fields.line')}
        actions={
          <Button variant="primary" onPress={() => open()}>
            <Icon name="plus" size={16} /> {say('record.fields.add')}
          </Button>
        }
      />
      {toast}
      {loadError && <Notice className="os-gap">{loadError}</Notice>}
      {error && !editing && <Notice className="os-gap">{error}</Notice>}

      <ListCard<View>
        title={say('settings.fields.list')}
        count={loaded ? say('record.fields.count', { n: active.length }) : undefined}
        chips={
          off.length > 0
            ? [
                { value: 'active', label: say('settings.rec.inUse'), count: active.length },
                { value: 'off', label: say('settings.rec.off'), count: off.length },
              ]
            : undefined
        }
        chip={view}
        onChip={setView}
      >
        {!loaded ? (
          <LoadingLine />
        ) : shown.length === 0 ? (
          <EmptyState title={say('record.fields.none')} />
        ) : (
          <ListTable
            label={say('settings.fields.list')}
            cols="minmax(220px, 1.6fr) minmax(140px, 1fr) 140px 72px"
            head={[say('record.col.detail'), say('record.col.shownOn'), say('record.col.kept'), '']}
          >
            {shown.map((f, i) => row(f, i, shown, view === 'active'))}
          </ListTable>
        )}
      </ListCard>

      {!organisation && <BookingRetention flash={flash} />}
    </>
  )
}

/**
 * How long a booking's name and phone are kept after its day.
 *
 * Moved here from the Appointments screen in the design: an operational screen is
 * the wrong place to decide how long personal data lives, and this is the screen
 * where every other such decision is made. The server keeps it for the signed-in
 * person's own organisation only (handleSetAppointmentPolicy), which is why it is
 * not offered while an installation owner looks at another organisation. Read by
 * whoever brings bookings in; a person the server refuses simply does not see it.
 */
function BookingRetention({ flash }: { flash: (text: string) => void }) {
  const { lang, say } = useStaffText()
  const [days, setDays] = useState<number | null>(null)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const readsPolicy = useMayRead('appointmentPolicy')
  useEffect(() => {
    if (!readsPolicy) return
    api
      .appointmentPolicy()
      .then((policy) => {
        setDays(policy.keepDays)
        setTyped(String(policy.keepDays))
      })
      .catch(() => setDays(null))
  }, [readsPolicy])

  if (days === null) return null
  const n = Number(typed)
  const bad = !/^\d+$/.test(typed) || n < 1 || n > MAX_RETENTION_DAYS

  return (
    <SettingsCard label={say('record.fields.bookings')}>
      <SettingRow
        as="form"
        control
        label={say('record.fields.bookings')}
        description={say('record.fields.bookingsHint')}
        status={
          (bad || error) && (
          <>
            {bad && <span className="record-error record-booking__err">{say('record.fields.bookingsBad', { max: MAX_RETENTION_DAYS })}</span>}
            {error && <span className="record-error record-booking__err">{error}</span>}
          </>
          )
        }
        onSubmit={(e) => {
          e.preventDefault()
          if (bad || busy) return
          setBusy(true)
          setError('')
          api
            .setAppointmentPolicy(n)
            .then((policy) => {
              setDays(policy.keepDays)
              setTyped(String(policy.keepDays))
              flash(say('record.fields.bookingSaved', { n: policy.keepDays }))
            })
            .catch((err) => setError(explain(err, lang, 'common.unreachable')))
            .finally(() => setBusy(false))
        }}
      >
        <span className="record-booking__days">
          <Input
            aria-label={say('record.fields.bookingsDays')}
            inputMode="numeric"
            value={typed}
            aria-invalid={bad || undefined}
            onChange={(e) => setTyped(e.target.value.trim())}
          />
          {say('record.fields.days')}
          {String(days) !== typed && (
            <Button type="submit" variant="secondary" size="sm" isDisabled={bad || busy}>
              {say('record.save')}
            </Button>
          )}
        </span>
      </SettingRow>
    </SettingsCard>
  )
}

function FieldDrawer({
  editing,
  all,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  editing: { field: Draft; isNew: boolean }
  all: VisitField[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Draft) => void
  onDelete: (field: Draft) => void
}) {
  const { lang, say } = useStaffText()
  const { isNew } = editing
  const [draft, setDraft] = useState(editing.field)
  const [choice, setChoice] = useState('')
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  const key = draft.key ?? ''
  const type = draft.type ?? 'text'
  const choices = draft.choices ?? []
  const keyBad = isNew && key !== '' && !validKey(key)
  const keyTaken = isNew && all.some((f) => f.key === key)
  const days = draft.retentionDays ?? 0
  const daysProblem = retentionProblem(days, lang)
  const choicesBad = type === 'choice' && choices.length === 0
  // A phone number is never printed on the ticket nor shown on the hall board
  // (catalog.Field.Validate): said by switching those two off, not by a refusal.
  const publicSurface = (s: FieldSurface) => s === 'ticket' || s === 'display'
  const phoneShownInPublic = type === 'phone' && draft.surfaces.some(publicSurface)
  const valid = validKey(key) && !keyTaken && hasName(draft.name) && !daysProblem && !choicesBad && !phoneShownInPublic

  const addChoice = () => {
    const c = choice.trim()
    if (c && !choices.includes(c)) set({ choices: [...choices, c] })
    setChoice('')
  }
  const flip = (s: FieldSurface) =>
    set({ surfaces: draft.surfaces.includes(s) ? draft.surfaces.filter((x) => x !== s) : [...draft.surfaces, s] })

  return (
    <InnerPage
      back={say('admin.section.fields')}
      title={isNew ? say('record.fields.new') : nameIn(draft.name, lang, key)}
      onClose={onClose}
      valid={valid}
      busy={busy}
      submitLabel={isNew ? say('record.fields.add') : say('record.save')}
      onSubmit={() => onSave(draft)}
      extra={
        isNew ? undefined : (
          <Confirm
            ask={say('record.fields.delete')}
            question={say('record.fields.deleteAsk', { key })}
            yes={say('record.fields.deleteYes')}
            keep={say('record.keep')}
            busy={busy}
            onYes={() => onDelete(draft)}
          />
        )
      }
    >
      {error && <Notice>{error}</Notice>}

      {isNew ? (
        <label className="field">
          <span title={say('record.fields.keyFixed')}>{say('record.fields.key')}</span>
          <Input
            className="record-code"
            value={key}
            placeholder={EXAMPLE_KEY}
            spellCheck={false}
            aria-invalid={keyBad || keyTaken || undefined}
            onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/\s+/g, '') })}
          />
          {keyBad && <small className="record-error">{say('record.fields.keyShape')}</small>}
          {keyTaken && <small className="record-error">{say('record.fields.keyTaken', { key })}</small>}
        </label>
      ) : (
        <div className="field">
          <span>{say('record.fields.key')}</span>
          <p className="record-keyfixed">{key}</p>
        </div>
      )}

      <NamesField legend={say('record.fields.question')} value={draft.name} onChange={(name) => set({ name })} />

      <div className="record-two">
        <div className="field">
          <span title={type === 'phone' ? say('record.fields.phoneHint') : undefined}>{say('record.fields.answerType')}</span>
          <ChoiceSelect
            label={say('record.fields.answerType')}
            value={type}
            onChange={(next) => set({ type: next as FieldType })}
            options={FIELD_TYPES.map((x) => ({ value: x.value, label: say(x.label) }))}
          />
        </div>
        {type === 'text' && (
          <label className="field record-narrow">
            <span title={say('record.fields.maxLengthHint')}>{say('record.fields.maxLength')}</span>
            <Input
              type="number"
              min={0}
              value={draft.maxLen ?? 0}
              onChange={(e) => set({ maxLen: Math.max(0, Number(e.target.value) || 0) })}
            />
          </label>
        )}
      </div>

      {type === 'choice' && (
        <fieldset className="field">
          <legend>{say('record.fields.choices')}</legend>
          {choices.length > 0 && (
            <ul className="picklist">
              {choices.map((c) => (
                <li key={c} className="picklist__row">
                  <span className="picklist__name">{c}</span>
                  <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    onPress={() => set({ choices: choices.filter((x) => x !== c) })}
                    aria-label={say('record.fields.removeChoice', { choice: c })}
                  >
                    <Icon name="close" size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="record-addchoice">
            <Input
              fullWidth
              value={choice}
              aria-label={say('record.fields.addChoice')}
              placeholder={say('record.fields.addChoice')}
              onChange={(e) => setChoice(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addChoice()
                }
              }}
            />
            <Button variant="secondary" isDisabled={!choice.trim()} onPress={addChoice}>
              <Icon name="plus" size={16} /> {say('record.add')}
            </Button>
          </div>
          {choicesBad && <small>{say('record.fields.choicesNeeded')}</small>}
        </fieldset>
      )}

      <fieldset className="field">
        <legend>{say('record.fields.shownOn')}</legend>
        {SURFACES.map((s) => {
          const barred = type === 'phone' && publicSurface(s.value)
          return (
            <Toggle
              key={s.value}
              title={say(s.label)}
              status={barred ? say('record.fields.notForPhone') : undefined}
              hint={s.hint ? say(s.hint) : undefined}
              checked={draft.surfaces.includes(s.value)}
              // Left switchable while on, so a phone field that arrived showing
              // on a public surface can be put right here.
              disabled={barred && !draft.surfaces.includes(s.value)}
              onChange={() => flip(s.value)}
            />
          )
        })}
        {draft.surfaces.length === 0 && <small>{say('record.fields.storedNotShown')}</small>}
      </fieldset>

      <fieldset className="field">
        <legend>{say('record.fields.personalData')}</legend>
        <Toggle
          title={say('record.fields.isPersonal')}
          hint={say('record.fields.personalHint')}
          checked={Boolean(draft.personal)}
          onChange={(personal) => set({ personal })}
        />
        {type === 'phone' && !draft.personal && <p className="record-warn">{say('record.fields.phoneIsPersonal')}</p>}
        <label className="field record-days">
          <span title={say('record.fields.zeroKeeps')}>{say('record.fields.keptFor')}</span>
          <span className="record-days__input">
            <Input
              type="number"
              min={0}
              max={MAX_RETENTION_DAYS}
              value={days}
              aria-invalid={Boolean(daysProblem) || undefined}
              onChange={(e) => set({ retentionDays: e.target.value === '' ? 0 : Number(e.target.value) })}
            />
            <span>{say('record.fields.daysAfterIssue')}</span>
          </span>
          {daysProblem && <small className="record-error">{daysProblem}</small>}
        </label>
        {draft.personal && days === 0 && !daysProblem && <p className="record-warn">{say('record.fields.noLimit')}</p>}
      </fieldset>

      {!isNew && (
        <>
          <Toggle
            title={say('record.fields.storedOnNew')}
            hint={say('record.fields.storedOnNewHint')}
            checked={draft.active ?? true}
            onChange={(active) => set({ active })}
          />
          <EraseValue fieldId={draft.id!} fieldKey={key} />
        </>
      )}
    </InnerPage>
  )
}

/**
 * Erasing one value on request, wherever it is stored under this key.
 *
 * Its own state and its own call, separate from the draft: this is not a change
 * to the declaration, and Cancel on the drawer must not undo what it did. Enter in
 * its box erases rather than submitting the drawer.
 */
function EraseValue({ fieldId, fieldKey }: { fieldId: string; fieldKey: string }) {
  const { lang, say } = useStaffText()
  const [value, setValue] = useState('')
  const [said, setSaid] = useState('')
  const [busy, setBusy] = useState(false)
  const erase = async () => {
    if (!value.trim() || busy) return
    setBusy(true)
    try {
      const { erased } = await api.eraseFieldValue(fieldId, value)
      setSaid(erasedSentence(fieldKey, erased, lang))
      setValue('')
    } catch (err) {
      setSaid(explain(err, lang, 'catalogue.fields.eraseFailed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="field">
      <span title={say('record.fields.eraseHint')}>{say('record.fields.erase')}</span>
      <div className="record-addchoice">
        <Input
          fullWidth
          value={value}
          aria-label={say('record.fields.eraseValue')}
          placeholder={say('record.fields.eraseValue')}
          onChange={(e) => {
            setValue(e.target.value)
            setSaid('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void erase()
            }
          }}
        />
        <Button variant="danger" isDisabled={busy || !value.trim()} onPress={() => void erase()}>
          {say('record.fields.eraseButton')}
        </Button>
      </div>
      {said && <small role="status">{said}</small>}
    </div>
  )
}
